import { useApolloClient } from '@apollo/client'
import axios from 'axios'
import fileDownload from 'js-file-download'
import { chunk } from 'lodash'
import { isMobile } from 'mobile-device-detect'
import { BucketMediaSharedFragmentFragmentDoc } from '../../../graphql/generated'
import logger from '../../../utils/logger'

export default function useBucketDownload() {
  const client = useApolloClient()

  const downloadSingle = (id: string) => {
    const media = client.readFragment({ id: `BucketMedia:${id}`, fragment: BucketMediaSharedFragmentFragmentDoc })

    if (!media?.signedUrl) return

    if (isMobile) {
      window.open(media.signedUrl, '_blank')
      return
    }

    // We have tried this a variety of ways in the past,
    // but this appears to be the most reliable for now
    const a = document.createElement('a')
    a.href = media.signedUrl
    a.download = media.signedUrl
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const downloadMultiple = async (ids: string[], num: number, total: number) => {
    const results = ids.map(async (id) => {
      const media = client.readFragment({ id: `BucketMedia:${id}`, fragment: BucketMediaSharedFragmentFragmentDoc })
      if (!media?.signedUrl || !media.filename || !media.mediaType) return
      const response = await axios.get(media.signedUrl, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: media.mediaType || 'application/octet-stream' })
      if (media.filename) {
        fileDownload(blob, media.filename)
      } else {
        logger.error('Filename is missing for media:', id)
      }
    })
    await Promise.all(results)
    logger.debug(`Batch ${num} of ${total}`, new Date().toISOString())
  }

  const downloadChunk = async (chunks: string[][], num: number, total: number) => {
    const filesInProgress = chunks.pop()
    if (!filesInProgress) return
    await downloadMultiple(filesInProgress, num, total)
    await downloadChunk(chunks, num + 1, total)
  }

  const downloadMedia = async (mediaIds: string[]) => {
    if (mediaIds.length === 1) {
      downloadSingle(mediaIds[0])
      return
    }

    const chunks = chunk(mediaIds, 5)
    await downloadChunk(chunks, 1, chunks.length)
  }

  return { downloadMedia }
}
